import React, { useEffect } from 'react';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';

interface ParameterPreservingRedirectProps {
  /**
   * The target path pattern to redirect to (e.g., "/users/:userId/profile")
   */
  to: string;
  /**
   * Whether to push a new entry to history or replace the current one
   * @default 'replace'
   */
  method?: 'push' | 'replace';
  /**
   * Whether to preserve the query string from the current URL
   * @default true
   */
  preserveQuery?: boolean;
  /**
   * Whether to preserve the hash fragment from the current URL
   * @default true
   */
  preserveHash?: boolean;
  /**
   * Whether to preserve the location state from the current navigation
   * @default true
   */
  preserveState?: boolean;
}

const ParameterPreservingRedirect: React.FC<ParameterPreservingRedirectProps> = ({
  to,
  method = 'replace',
  preserveQuery = true,
  preserveHash = true,
  preserveState = true,
}) => {
  const history = useHistory();
  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    try {
      // Generate the new path with the current URL parameters
      const newPath = generatePath(to, params);

      // Build the complete URL with query string and hash if needed
      let fullPath = newPath;

      if (preserveQuery && location.search) {
        fullPath += location.search;
      }

      if (preserveHash && location.hash) {
        fullPath += location.hash;
      }

      // Create the navigation options
      const navigationOptions: { state?: any } = {};

      if (preserveState && location.state) {
        navigationOptions.state = location.state;
      }

      // Perform the redirect
      if (method === 'push') {
        history.push(fullPath, navigationOptions.state);
      } else {
        history.replace(fullPath, navigationOptions.state);
      }
    } catch (error) {
      console.error('Error during parameter-preserving redirect:', error);
      // Fallback to simple redirect without parameters
      history.replace(to);
    }
  }, [to, method, preserveQuery, preserveHash, preserveState, history, location, params]);

  // Render nothing - this component only handles the redirect side effect
  return null;
};

export default ParameterPreservingRedirect;
